import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DocumentsAreaSections } from '../../../../model';
import { getTotalDocumentsCount } from '../../../../model/documents/unread-documents-count';
import {
  addAllBasicExtraReducers,
  addPendingAndErrorExtraReducers,
} from '../../../../utils/action-builder-utils';
import {
  downloadDocumentsFile,
  markDocumentAsRead,
  retrieveUnreadDocumentsCounts,
} from './common-thunks';
import { DocumentsAreaCommonState } from './common-types';

const initialState: DocumentsAreaCommonState = {
  selectedSection: 'DOCUMENTS',
  totalUnreadCounts: {
    DOCUMENTS: 0,
    INFO_OFFER: 0,
    COMMUNICATIONS: 0,
  },
  totalUnreadCountsRequestStatus: 'idle',
  navigationMustReset: false,
  markDocumentAsReadRequestStatus: 'idle',
  fileDownloadedRequestStatus: 'idle',
};

const documentsAreaCommonSlice = createSlice({
  name: 'features/documents/common',
  initialState: initialState,
  reducers: {
    resetWalletCommonState() {
      return initialState;
    },
    setSelectedSection(state, action: PayloadAction<DocumentsAreaSections>) {
      state.selectedSection = action.payload;
    },
    setNavigationMustReset(state, action: PayloadAction<boolean>) {
      state.navigationMustReset = action.payload;
    },
    clearMarkDocumentAsRead(state) {
      state.markDocumentAsReadRequestStatus = 'idle';
      state.fileDownloadedError = undefined;
    },
    clearDownloadFile(state) {
      state.fileDownloadedError = undefined;
      state.fileDownloadedRequestStatus = 'idle';
      state.fileDownloaded = undefined;
    },
  },
  extraReducers: (builder) => {
    addPendingAndErrorExtraReducers(
      builder,
      retrieveUnreadDocumentsCounts,
      'totalUnreadCounts'
    );
    builder.addCase(
      retrieveUnreadDocumentsCounts.fulfilled,
      (state, action) => {
        state.totalUnreadCountsRequestStatus = 'succeeded';
        state.totalUnreadCounts = {
          DOCUMENTS: getTotalDocumentsCount(action.payload),
          INFO_OFFER: action.payload.totalOffers,
          COMMUNICATIONS: action.payload.totalCommunications,
        };
      }
    );
    builder.addCase(markDocumentAsRead.pending, (state) => {
      state.markDocumentAsReadRequestStatus = 'loading';
    });
    builder.addCase(markDocumentAsRead.rejected, (state, action) => {
      state.markDocumentAsReadRequestStatus = 'failed';
      state.markDocumentAsReadError = action.payload;
    });
    builder.addCase(markDocumentAsRead.fulfilled, (state, action) => {
      state.markDocumentAsReadRequestStatus = 'succeeded';
      state.totalUnreadCounts[action.payload] = Math.max(
        state.totalUnreadCounts[action.payload] - 1,
        0
      );
    });
    addAllBasicExtraReducers(builder, downloadDocumentsFile, 'fileDownloaded');
  },
});

export const documentsAreaCommonSliceActions = documentsAreaCommonSlice.actions;

export default documentsAreaCommonSlice.reducer;
